/**
 * Calendar date picker for selecting the trip start date.
 * Traditional month grid with month/year navigation and year selector.
 */

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function parseDateString(dateStr: string): { year: number; month: number; day: number } {
  const [year, month, day] = dateStr.split('-').map(Number);
  return { year, month: month - 1, day };
}

function toDateString(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

interface TravelDatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (date: string) => void;
  children: React.ReactNode;
}

export function TravelDatePicker({ value, onChange, children }: TravelDatePickerProps) {
  const [open, setOpen] = useState(false);
  const parsed = parseDateString(value);
  const [viewYear, setViewYear] = useState(parsed.year);
  const [viewMonth, setViewMonth] = useState(parsed.month);

  // Reset view to selected date when opening
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      const p = parseDateString(value);
      setViewYear(p.year);
      setViewMonth(p.month);
    }
    setOpen(nextOpen);
  };

  // Year range: 10 years before/after current value
  const yearOptions = useMemo(() => {
    const center = parsed.year;
    const years: number[] = [];
    for (let y = center - 10; y <= center + 10; y++) {
      years.push(y);
    }
    return years;
  }, [parsed.year]);

  // Build calendar grid for the viewed month
  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells: Array<{ day: number; month: number; year: number; outside: boolean }> = [];

    // Previous month fill
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const m = viewMonth === 0 ? 11 : viewMonth - 1;
      const y = viewMonth === 0 ? viewYear - 1 : viewYear;
      cells.push({ day: d, month: m, year: y, outside: true });
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, month: viewMonth, year: viewYear, outside: false });
    }

    // Next month fill to complete the grid
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      const m = viewMonth === 11 ? 0 : viewMonth + 1;
      const y = viewMonth === 11 ? viewYear + 1 : viewYear;
      for (let d = 1; d <= remaining; d++) {
        cells.push({ day: d, month: m, year: y, outside: true });
      }
    }

    return cells;
  }, [viewYear, viewMonth]);

  const navigateMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y--;
    }
    if (m > 11) {
      m = 0;
      y++;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  const handleSelect = (cell: { day: number; month: number; year: number }) => {
    onChange(toDateString(cell.year, cell.month, cell.day));
    setOpen(false);
  };

  const isSelected = (cell: { day: number; month: number; year: number }) =>
    cell.day === parsed.day && cell.month === parsed.month && cell.year === parsed.year;

  const today = new Date();
  const isToday = (cell: { day: number; month: number; year: number }) =>
    cell.day === today.getDate() &&
    cell.month === today.getMonth() &&
    cell.year === today.getFullYear();

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        {/* Month/Year header */}
        <div className="flex items-center justify-between mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => navigateMonth(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            <Select value={String(viewMonth)} onValueChange={(v) => setViewMonth(Number(v))}>
              <SelectTrigger className="h-7 w-[110px] text-sm font-medium border-none shadow-none px-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((name, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={String(viewYear)} onValueChange={(v) => setViewYear(Number(v))}>
              <SelectTrigger className="h-7 w-[75px] text-sm font-medium border-none shadow-none px-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => navigateMonth(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAY_HEADERS.map((d) => (
            <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7">
          {calendarDays.map((cell, i) => (
            <button
              key={i}
              onClick={() => handleSelect(cell)}
              className={`
                h-8 w-8 rounded-md text-sm flex items-center justify-center transition-colors
                ${cell.outside ? 'text-muted-foreground/40' : ''}
                ${isSelected(cell) ? 'bg-primary text-primary-foreground font-semibold' : ''}
                ${!isSelected(cell) && isToday(cell) ? 'border border-primary text-primary' : ''}
                ${!isSelected(cell) && !cell.outside ? 'hover:bg-accent' : ''}
                ${!isSelected(cell) && cell.outside ? 'hover:bg-accent/50' : ''}
              `}
            >
              {cell.day}
            </button>
          ))}
        </div>

        {/* Today shortcut */}
        <div className="mt-2 pt-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 text-xs"
            onClick={() => {
              const t = new Date();
              onChange(toDateString(t.getFullYear(), t.getMonth(), t.getDate()));
              setOpen(false);
            }}
          >
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
